/**
 * Local ↔ cloud collection linking (device snapshots).
 *
 * Conflict rule (MVP): local is draft until explicit "Sync to cloud".
 * Cloud saves to a linked collection overwrite the local snapshot copy (last write wins on cloud save).
 */

import { generateLocalWorkspaceId } from '../utils/workspaceStorage';

export function ensureLocalUid(collection) {
  if (!collection) return collection;
  if (collection.local_uid) return collection;
  const uid =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : generateLocalWorkspaceId();
  return { ...collection, local_uid: uid };
}

export function linkToCloud(localCollection, cloudCollection) {
  const base = ensureLocalUid(localCollection);
  return {
    ...base,
    cloud_collection_id: cloudCollection.id,
    link_status: 'linked',
    last_synced_at: new Date().toISOString(),
  };
}

export function markLocalOnly(collection) {
  const base = ensureLocalUid(collection);
  return {
    ...base,
    link_status: base.cloud_collection_id ? 'linked' : 'local_only',
  };
}

export function stripDeviceLocalFields(collection) {
  if (!collection) return collection;
  const {
    _deviceLocal,
    _localWorkspaceId,
    _localWorkspaceName,
    ...rest
  } = collection;
  return rest;
}

export function deepCopyCollectionForLocal(cloudCollection, options = {}) {
  const { newCollectionId, localWorkspaceId } = options;
  const colId =
    newCollectionId ||
    (typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `local-col-${Date.now()}`);

  const base = ensureLocalUid({
    ...stripDeviceLocalFields(cloudCollection),
    id: colId,
    cloud_collection_id: cloudCollection.id,
    link_status: 'linked',
    last_synced_at: new Date().toISOString(),
    requests: (cloudCollection.requests || []).map((r, ri) => ({
      ...r,
      id:
        localWorkspaceId != null
          ? `${localWorkspaceId}-req-${colId}-${ri}`
          : `req-${colId}-${ri}`,
    })),
  });
  return base;
}

export function findLinkedLocalCopy(allDeviceCollections, cloudCollectionId) {
  if (!cloudCollectionId) return null;
  return (
    allDeviceCollections.find(
      (c) =>
        String(c.cloud_collection_id) === String(cloudCollectionId) ||
        String(c.id) === String(cloudCollectionId)
    ) || null
  );
}

export function copyCloudCollectionToSnapshot(snapshot, cloudCollection, options = {}) {
  const { replaceExisting = true } = options;
  const copy = deepCopyCollectionForLocal(cloudCollection, {
    localWorkspaceId: options.localWorkspaceId,
  });
  const cols = snapshot.collections || [];
  const idx = cols.findIndex(
    (c) =>
      (copy.local_uid && c.local_uid === copy.local_uid) ||
      String(c.cloud_collection_id) === String(cloudCollection.id)
  );
  let nextCollections;
  if (idx >= 0 && replaceExisting) {
    nextCollections = [...cols];
    nextCollections[idx] = copy;
  } else {
    nextCollections = [...cols, copy];
  }
  return { ...snapshot, collections: nextCollections };
}

export function enrichCloudCollectionsWithLinks(cloudCollections, deviceLocalCollections) {
  return (cloudCollections || []).map((c) => {
    const localCopy = findLinkedLocalCopy(deviceLocalCollections, c.id);
    if (!localCopy) return c;
    return {
      ...c,
      link_status: 'linked',
      local_uid: localCopy.local_uid ?? c.local_uid,
      cloud_collection_id: c.id,
    };
  });
}

/** Badge kind for sidebar rows (not used for separate device-local sidebar section). */
export function collectionLinkBadge(collection, { isLocalWorkspace = false } = {}) {
  if (!collection) return null;
  if (collection.link_status === 'linked' || (collection.cloud_collection_id && isLocalWorkspace)) {
    return 'linked';
  }
  return null;
}
